import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import { Mail, AlertCircle, CheckCircle } from 'lucide-react';

// VALUTTEN Logo component
const ValuttenLogo = () => (
  <img
    src="/valutten-logo.jpg"
    alt="VALUTTEN"
    className="h-12 w-auto object-contain"
  />
);

const AdminPasswordResetPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      // Call sendPasswordReset Cloud Function with isAdmin flag
      const sendPasswordReset = httpsCallable(functions, 'sendPasswordReset');
      const result = await sendPasswordReset({ email, isAdmin: true });

      if ((result.data as any).success) {
        setMessage('Password reset email sent! Check your inbox for further instructions.');
      } else {
        setError((result.data as any).message || 'Failed to send reset email. Please try again.');
      }
    } catch (err: any) {
      console.error('Password reset error:', err);

      // Handle rate limiting
      if (err.code === 'functions/resource-exhausted' || err.message.includes('rate limit')) {
        setError('Too many reset attempts. Please wait a few minutes and try again.');
      } else if (err.code === 'functions/unauthenticated') {
        setError('Email address not found. Please check and try again.');
      } else {
        setError('Failed to send reset email. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-900 bg-gradient-to-br from-indigo-900/50 via-gray-900 to-fuchsia-900/50 text-white">
      {/* Background decorative elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-0 -left-1/4 w-96 h-96 bg-indigo-600/30 rounded-full filter blur-3xl opacity-50 animate-pulse"></div>
        <div className="absolute bottom-0 -right-1/4 w-96 h-96 bg-fuchsia-400/30 rounded-full filter blur-3xl opacity-50 animation-delay-4000 animate-pulse"></div>
      </div>

      <div className="w-full max-w-lg p-8 rounded-2xl shadow-2xl bg-black/30 backdrop-blur-xl border border-white/10 z-10">
        {/* VALUTTEN Header */}
        <div className="flex flex-col items-center justify-center mb-6">
          <ValuttenLogo />
          <div className="mt-4 w-14 h-14 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
            <Mail className="w-7 h-7 text-fuchsia-400" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-2 text-white">Reset Admin Password</h1>
        <p className="text-center text-slate-400 text-sm mb-6">
          Enter your admin email address and we'll send you a link to reset your password.
        </p>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {message && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-green-400 text-sm">{message}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
              Email address
            </label>
            <input
              id="email"
              type="email"
              placeholder="admin@example.com"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-fuchsia-500 focus:ring-offset-2 focus:ring-offset-slate-900"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </span>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>

        <p className="text-center mt-6 text-slate-400 text-sm">
          Remember your password?{' '}
          <Link to="/login" className="text-fuchsia-400 hover:text-fuchsia-300 font-medium">
            Back to Login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AdminPasswordResetPage;
